import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { BRAND } from '../data/content';
import { MaskedRevealOnView, FadeUp } from './motion/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', phone: '', company: '', product_interest: '', message: '' };

const productOptions = ['Round Bright Bars', 'Hexagonal Bright Bars', 'Square Bright Bars', 'Flat Bright Bars', 'General enquiry'];

const Contact = () => {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error('Please fill name, email, phone and message.');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/inquiries`, form);
            toast.success('Inquiry received — our team will reach out shortly.');
            setForm(initial);
        } catch (err) {
            console.error(err);
            toast.error('Something went wrong. Please try calling us instead.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="bg-[#F4F5F6] text-[#0A0A0B] py-28 md:py-40 border-t border-[#0A0A0B]/10">
            <div className="mx-auto max-w-[1500px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 mb-16">
                    <div className="col-span-12 md:col-span-4">
                        <span className="overline text-[#FF3B00]">Contact · 05</span>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <MaskedRevealOnView
                            className="h-display text-[10vw] md:text-[6vw] leading-[0.9]"
                            lines={['Send us your', 'spec sheet.']}
                        />
                        <FadeUp delay={0.15}>
                            <p className="mt-6 max-w-2xl text-[#0A0A0B]/70 text-base md:text-lg leading-relaxed">
                                Drop a message with your grade, size and quantity — we'll respond within one business day with pricing and lead time.
                            </p>
                        </FadeUp>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-8 lg:gap-16 border-t border-[#0A0A0B]/15 pt-14">
                    {/* Info column */}
                    <div className="col-span-12 lg:col-span-5">
                        <FadeUp>
                            <div className="overline text-[#0A0A0B]/50 mb-4">Head office · plant</div>
                            <div className="h-display text-3xl md:text-4xl mb-8 max-w-md leading-tight">
                                {BRAND.name}
                            </div>
                            <div className="space-y-6 text-sm md:text-base">
                                <div className="flex gap-4">
                                    <MapPin size={18} className="text-[#FF3B00] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                    <div>
                                        {BRAND.addressLine1}<br />
                                        {BRAND.addressLine2}<br />
                                        {BRAND.addressLine3}
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <Phone size={18} className="text-[#FF3B00] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                    <div className="font-mono">
                                        {BRAND.phones.map((p) => (
                                            <a key={p} href={`tel:${p.replace(/\s/g, '')}`} data-testid={`phone-${p}`} className="block hover:text-[#FF3B00]">{p}</a>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <Mail size={18} className="text-[#FF3B00] mt-1 flex-shrink-0" strokeWidth={1.5} />
                                    <div className="font-mono">
                                        {BRAND.emails.map((em) => (
                                            <a key={em} href={`mailto:${em}`} data-testid={`email-${em}`} className="block hover:text-[#FF3B00]">{em}</a>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-12 pt-8 border-t border-[#0A0A0B]/15">
                                <div className="overline text-[#0A0A0B]/50 mb-3">Working hours</div>
                                <div className="font-mono text-sm">Mon – Sat · 09:30 – 18:30 IST</div>
                            </div>
                        </FadeUp>
                    </div>

                    {/* Form column */}
                    <motion.form
                        onSubmit={submit}
                        data-testid="contact-form"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 lg:col-span-7 grid grid-cols-2 gap-x-6 gap-y-8"
                    >
                        <Field label="Name" name="name" value={form.name} onChange={onChange} testId="input-name" required span={1} />
                        <Field label="Company" name="company" value={form.company} onChange={onChange} testId="input-company" span={1} />
                        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testId="input-email" required span={1} />
                        <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="input-phone" required span={1} />

                        <div className="col-span-2">
                            <label className="overline text-[#0A0A0B]/50 block mb-3">Product interest</label>
                            <div className="flex flex-wrap gap-2">
                                {productOptions.map((opt) => (
                                    <button
                                        type="button"
                                        key={opt}
                                        data-testid={`interest-${opt.split(' ')[0].toLowerCase()}`}
                                        onClick={() => setForm((f) => ({ ...f, product_interest: opt }))}
                                        className={`px-4 py-2.5 text-xs font-mono uppercase tracking-[0.15em] border transition-colors ${
                                            form.product_interest === opt
                                                ? 'bg-[#0A0A0B] text-white border-[#0A0A0B]'
                                                : 'border-[#0A0A0B]/25 text-[#0A0A0B] hover:border-[#0A0A0B]'
                                        }`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="col-span-2">
                            <label className="overline text-[#0A0A0B]/50 block mb-3">Message · include grade · size · quantity</label>
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                data-testid="input-message"
                                rows={5}
                                required
                                className="w-full bg-transparent border-b border-[#0A0A0B]/25 py-3 text-base focus:outline-none focus:border-[#FF3B00] transition-colors resize-none"
                                placeholder="e.g., EN1A · Ø 20 mm h9 · 5 MT — required by 15 Jan"
                            />
                        </div>

                        <div className="col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mt-4">
                            <p className="text-xs text-[#0A0A0B]/50 font-mono max-w-xs">
                                We reply within one business day. Your details are never shared.
                            </p>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="submit-inquiry"
                                className="btn-invert inline-flex items-center gap-3 h-14 px-10 bg-[#FF3B00] text-white text-[11px] font-mono uppercase tracking-[0.24em] hover:text-[#FF3B00] disabled:opacity-60"
                            >
                                {loading ? 'Sending…' : 'Send Inquiry'} <ArrowRight size={16} />
                            </button>
                        </div>
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, name, value, onChange, type = 'text', testId, required, span = 1 }) => (
    <div className={span === 1 ? 'col-span-2 md:col-span-1' : 'col-span-2'}>
        <label className="overline text-[#0A0A0B]/50 block mb-3">{label}{required && <span className="text-[#FF3B00]"> *</span>}</label>
        <input
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            data-testid={testId}
            required={required}
            className="w-full bg-transparent border-b border-[#0A0A0B]/25 py-3 text-base focus:outline-none focus:border-[#FF3B00] transition-colors"
        />
    </div>
);

export default Contact;
